import { useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@/stores/authStore'
import { useSocketStore, registerQueryInvalidator } from '@/stores/socketStore'
import Layout from '@/components/Layout'
import LoginPage from '@/pages/LoginPage'
import DashboardPage from '@/pages/DashboardPage'
import DevicesPage from '@/pages/DevicesPage'
import SessionsPage from '@/pages/SessionsPage'
import CustomersPage from '@/pages/CustomersPage'
import SettingsPage from '@/pages/SettingsPage'
import UsersPage from '@/pages/UsersPage'
import DownloadsPage from '@/pages/DownloadsPage'

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-sc-bg-darkest">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-sc-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sc-text-muted">Loading...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

/**
 * SocketManager
 *
 * Lives inside the React tree so it can access the query client.
 * Registers the RQ invalidation callback and manages the socket lifecycle
 * based on auth state.
 */
function SocketManager() {
  const queryClient = useQueryClient()
  const token = useAuthStore((s) => s.token)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const initSocket = useSocketStore((s) => s.init)
  const destroySocket = useSocketStore((s) => s.destroy)

  // Wire up query invalidation once on mount
  useEffect(() => {
    registerQueryInvalidator((keys) => {
      keys.forEach((key) => queryClient.invalidateQueries({ queryKey: key }))
    })
  }, [queryClient])

  // Connect / disconnect based on auth state
  useEffect(() => {
    if (isAuthenticated && token) {
      initSocket(token)
    } else {
      destroySocket()
    }

    return () => {
      // Clean up when component unmounts (full page unload)
      destroySocket()
    }
  }, [isAuthenticated, token, initSocket, destroySocket])

  return null
}

function App() {
  return (
    <>
      <SocketManager />
      <Routes>
        <Route path="/login" element={<LoginPage />} />

        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="devices" element={<DevicesPage />} />
          <Route path="sessions" element={<SessionsPage />} />
          <Route path="customers" element={<CustomersPage />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="downloads" element={<DownloadsPage />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>

        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </>
  )
}

export default App
